import { useEffect, useMemo, useRef, useState } from 'react'
import { useStyles } from './KeyWordRowV2.styles'
import { useTheme } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { Grid, Typography } from '@mui/material'
import clsx from 'clsx'
import {
  CHART_AXIS_GROUPING_OPTIONS,
  ChartAxisGroupingSelection,
  DEFAULT_CHART_AXIS_GROUPING,
  computeHorizontalScrollLayout,
  formatAxisTickLabel,
  getBottomAxisTickLabelDate,
  getBottomAxisTickLabels,
  resolveChartAxis,
} from '../ResponsiveLineChart/chartAxisUtils'
import { ResponsiveLineChart } from '../ResponsiveLineChart/ResponsiveLineChart'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'
import {
  CustomLegends,
  type LegendObject,
} from '../../FlyoutOverview/CustomLegends/CustomLegends'
import {
  ButtonV4,
  LabraToolTipV2,
  NoteColors,
  NoteV2,
  PercentageUpDown,
  VerticalDivider,
} from '@labrav/react-components'
import { KeywordItem } from '../../../modules/mpsSeo/reducer'
import { isNonEmptyNumber } from '@labrav/common'
import { productDetailDate } from '../../../common/utils/productCreationUtil'
import { CustomTooltip } from '../CustomTooltip/CustomTooltip'
import {
  MOCK_KEYWORD_ITEM_FOR_CHART,
  USE_MOCK_GRAPH_DATA,
} from './KeyWordRowV2.mockData'
import {
  CHART_RANK_NOTE,
  RANKING_REFRESH_TOOLTIP_NOTE,
  formatShortChartDate,
  getCompetitorLegendIds,
  getRankTooltip,
  getRankingRefreshNote,
  getStartAndEndDate,
} from './KeyWordRowV2.utils'

export const KeyWordRowV2 = ({
  keywordItem: keywordItemProp,
  selectedLegends: selectedLegendsProp,
  onSelectedLegendsChange,
}: {
  keywordItem: KeywordItem
  selectedLegends?: string[]
  onSelectedLegendsChange?: (legends: string[]) => void
}) => {
  const keywordItem = USE_MOCK_GRAPH_DATA
    ? MOCK_KEYWORD_ITEM_FOR_CHART
    : keywordItemProp
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()
  const [groupingSelection, setGroupingSelection] =
    useState<ChartAxisGroupingSelection>(DEFAULT_CHART_AXIS_GROUPING)

  useEffect(() => {
    setGroupingSelection(DEFAULT_CHART_AXIS_GROUPING)
  }, [keywordItem.keywordName])

  const competitorLegendIds = useMemo(
    () => getCompetitorLegendIds(keywordItem),
    [keywordItem.keywordName, keywordItem.competitors]
  )
  const [internalSelectedLegends, setInternalSelectedLegends] = useState<
    string[]
  >(() => competitorLegendIds)

  useEffect(() => {
    if (selectedLegendsProp !== undefined) {
      return
    }

    if (!competitorLegendIds.length) {
      setInternalSelectedLegends([])
      return
    }

    setInternalSelectedLegends(prev =>
      prev.length === 0 ? competitorLegendIds : prev
    )
  }, [keywordItem.keywordName, competitorLegendIds, selectedLegendsProp])

  const selectedLegends = selectedLegendsProp ?? internalSelectedLegends

  const updateSelectedLegends = (nextSelection: string[]) => {
    if (onSelectedLegendsChange) {
      onSelectedLegendsChange(nextSelection)
      return
    }

    setInternalSelectedLegends(nextSelection)
  }

  const competitorList = keywordItem?.competitors?.map(
    ({ Id, ...competitorItem }) => ({
      ...competitorItem,
      id: Id || 'self',
      data: competitorItem?.data?.map(competitorDataItem => ({
        ...competitorDataItem,
        competitorName: competitorItem?.name,
        x: competitorDataItem?.createdAt,
        y: competitorDataItem?.rank,
      })),
    })
  )

  const competitorGraphList = competitorList?.filter(competitorItem =>
    selectedLegends?.includes(competitorItem?.id)
  )

  const theme = useTheme()
  const graphColors = [
    theme.palette.graphLines.red,
    theme.palette.graphLines.lavendar,
    theme.palette.graphLines.green,
    theme.palette.graphLines.orange,
    theme.palette.graphLines.brown,
    theme.palette.graphLines.pink,
    theme.palette.graphLines.yellow,
    theme.palette.graphLines.cyan,
    theme.palette.graphLines.darkGray,
    theme.palette.graphLines.teal,
    theme.palette.graphLines.lightBlue,
    theme.palette.graphLines.magenta,
    theme.palette.graphLines.limeGreen,
    theme.palette.graphLines.darkGold,
    theme.palette.activeField.main,
  ]

  const { tooltipMsg, upDown } = getRankTooltip(
    keywordItem?.selfRankChange,
    keywordItem?.selfInitialRank,
    keywordItem?.selfLatestRank
  )

  const handleLegendClick = (legend: LegendObject) => {
    if (legend?.id) {
      if (selectedLegends?.includes(legend?.id)) {
        updateSelectedLegends(
          selectedLegends.filter(legendItem => legendItem !== legend?.id)
        )
      } else {
        updateSelectedLegends([...selectedLegends, legend.id])
      }
    }
  }

  const selectedLegendIndexs = competitorList?.map(
    (competitorListItem, index) =>
      selectedLegends.includes(competitorListItem?.id) ? index : -1
  )
  const graphLinesColors = graphColors?.filter(
    (item, index) => selectedLegendIndexs[index] !== -1
  )

  const shouldShowRankAndUpDown =
    isNonEmptyNumber(keywordItem?.selfRankChange) &&
    keywordItem?.selfLatestRank &&
    !!keywordItem?.selfRankChange
  const { formattedStartDate, formattedEndDate } =
    getStartAndEndDate(competitorGraphList)
  const { xDomain, bottomAxisLayout } = resolveChartAxis(
    competitorGraphList ?? [],
    true,
    groupingSelection
  )
  const axisGrouping = bottomAxisLayout?.grouping ?? {
    isGrouped: true,
    interval: DEFAULT_CHART_AXIS_GROUPING,
  }

  const chartScrollRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    const chartContainer = chartScrollRef.current

    if (!chartContainer) {
      return
    }

    const updateContainerWidth = () => {
      setContainerWidth(chartContainer.clientWidth)
    }

    updateContainerWidth()

    const resizeObserver = new ResizeObserver(updateContainerWidth)
    resizeObserver.observe(chartContainer)

    return () => resizeObserver.disconnect()
  }, [])

  const { needsHorizontalScroll, minChartWidth } = useMemo(() => {
    const tickLabels = getBottomAxisTickLabels({
      bottomAxisLayout,
      xDomain,
      grouping: axisGrouping,
      timeZone,
      shortDateFormatter: formatShortChartDate,
    })

    return computeHorizontalScrollLayout({
      containerWidth,
      tickLabels,
    })
  }, [axisGrouping, bottomAxisLayout, containerWidth, timeZone, xDomain])

  const rankingRefreshNote = getRankingRefreshNote(
    keywordItem?.updatedAt,
    timeZone
  )

  return (
    <Grid container className={classes.marginTop}>
      <Grid
        className={classes.container}
        size={{
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
        }}
      >
        <div className={classes.rootContainer} data-testid={'keyword-item'}>
          <div className={classes.chartContent}>
            <div className={classes.subHeading}>
              <div className={classes.headerRow}>
                <div className={classes.overallSearch}>
                  Overall search ranking
                  <LabraToolTipV2
                    tooltipPlacement="top"
                    description={
                      <div>
                        <Typography className={classes.toolTip}>
                          Updated on:{' '}
                          {
                            productDetailDate(keywordItem?.updatedAt, timeZone)
                              .fullDateTime
                          }
                          <br />
                          {RANKING_REFRESH_TOOLTIP_NOTE}
                        </Typography>
                      </div>
                    }
                    tooltipPlacmentGap="10px"
                  >
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{
                        height: 16,
                        width: 16,
                        display: 'flex',
                        color: theme.palette.labraText.description,
                        marginBottom: '-2px',
                      }}
                    />
                  </LabraToolTipV2>
                </div>
                <VerticalDivider />
                <div className={classes.placementCenter}>
                  <LabraToolTipV2
                    tooltipPlacement="right"
                    description={tooltipMsg}
                  >
                    <div className={classes.rankSlot}>
                      <div
                        className={clsx(
                          classes.rankValue,
                          !isNonEmptyNumber(keywordItem?.selfLatestRank) &&
                            classes.rankValueReserved,
                          isNonEmptyNumber(keywordItem?.selfLatestRank)
                            ? clsx(
                                classes.keyword,
                                !shouldShowRankAndUpDown
                                  ? classes.rankingBorder
                                  : ''
                              )
                            : classes.notAvilable
                        )}
                      >
                        {isNonEmptyNumber(keywordItem?.selfLatestRank)
                          ? keywordItem?.selfLatestRank
                          : 'Not available'}
                      </div>
                      <div className={classes.rankChangeSlot}>
                        {shouldShowRankAndUpDown && (
                          <div className={classes.rankChangeBadge}>
                            <PercentageUpDown
                              percentage={Math.abs(keywordItem?.selfRankChange)}
                              isPercentage={false}
                              type={upDown}
                              showBackgroundColor={true}
                              fontSize="12px"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </LabraToolTipV2>
                </div>

                <NoteV2
                  backgroundColor={NoteColors.BLUE}
                  description={CHART_RANK_NOTE}
                />
              </div>

              <CustomLegends
                handleLegendClick={handleLegendClick}
                legends={competitorList.map((item, index) => ({
                  id: item?.id,
                  label: item?.name,
                  legendColor: graphColors[index],
                  legendSize: 'medium',
                  legendUpDown: item?.rankChange,
                  rank: item?.rank,
                  legendGrpahLineVisible: !!selectedLegends?.includes(item?.id),
                  legendUpDownTooltip: getRankTooltip(
                    item?.rankChange,
                    item?.initialRank,
                    item?.rank
                  ).tooltipMsg,
                }))}
                flexDirection="row"
                roundedBorder={true}
              />
            </div>
            <div className={classes.axisGroupingControls}>
              {CHART_AXIS_GROUPING_OPTIONS.map(option => {
                const isSelected = groupingSelection === option.value

                return (
                  <ButtonV4
                    key={option.value}
                    size="small"
                    variant="secondary"
                    selected={isSelected}
                    className={classes.axisGroupingButton}
                    testId={`axis-grouping-${option.value}`}
                    onClick={() => setGroupingSelection(option.value)}
                    backgroundColor={
                      isSelected ? theme.palette.grey[300] : undefined
                    }
                    borderColor={
                      isSelected ? theme.palette.grey[500] : undefined
                    }
                    textColor={
                      isSelected ? theme.palette.labraText.primary : undefined
                    }
                  >
                    {option.label}
                  </ButtonV4>
                )
              })}
            </div>
            <div className={classes.graphWrapper}>
              <div
                ref={chartScrollRef}
                className={clsx(
                  classes.chartScrollContainer,
                  needsHorizontalScroll && classes.chartScrollContainerActive
                )}
                data-testid="chart-scroll-container"
                data-scroll-enabled={needsHorizontalScroll ? 'true' : 'false'}
              >
                <div
                  className={classes.responsiveLineWrapper}
                  style={
                    needsHorizontalScroll
                      ? {
                          width: minChartWidth,
                          minWidth: minChartWidth,
                        }
                      : undefined
                  }
                  data-testid={'responsive-line-chart'}
                >
                  <ResponsiveLineChart
                    data={competitorGraphList}
                    customColor={theme.palette.labraText.tertiary}
                    reverse
                    pointerSize={8}
                    gridYValues={10}
                    leftTickValues={10}
                    bottomTickValues={true}
                    axisGroupingSelection={groupingSelection}
                    colors={graphLinesColors}
                    leftAxisLegend={'Rank'}
                    tooltip={point => {
                      return (
                        <CustomTooltip
                          point={point?.point}
                          color={point?.point?.color}
                          timeZone={timeZone}
                        />
                      )
                    }}
                    renderTick={item => {
                      const labelDate =
                        bottomAxisLayout &&
                        getBottomAxisTickLabelDate(
                          bottomAxisLayout,
                          Number(item.value)
                        )
                      const tickLineLength = theme.spacing(0.75)
                      const tickLabelOffset = theme.spacing(2.25)
                      const axisTickColor = theme.palette.labraText.primary

                      return (
                        <g transform={`translate(${item.x},${item.y})`}>
                          <line
                            x1={0}
                            y1={0}
                            x2={0}
                            y2={tickLineLength}
                            stroke={axisTickColor}
                            strokeWidth={1}
                          />
                          <text
                            x={0}
                            y={tickLabelOffset}
                            dominantBaseline="hanging"
                            textAnchor="middle"
                            style={{
                              fill: axisTickColor,
                              fontSize: 12,
                              fontWeight: 500,
                            }}
                          >
                            {labelDate
                              ? formatAxisTickLabel({
                                  date: labelDate,
                                  sortedXDomain: xDomain,
                                  grouping: axisGrouping,
                                  timeZone,
                                  shortDateFormatter: formatShortChartDate,
                                })
                              : ''}
                          </text>
                        </g>
                      )
                    }}
                  />
                </div>
              </div>
              <div className={classes.chartFooterNotes}>
                {formattedStartDate && formattedEndDate && (
                  <Typography
                    className={classes.chartTimeframeLegend}
                    data-testid="chart-timeframe-legend"
                  >
                    {`Rank timeframe: ${formattedStartDate} to ${formattedEndDate}`}
                  </Typography>
                )}
                <Typography
                  className={classes.chartTimeframeLegend}
                  data-testid="chart-ranking-refresh-note"
                >
                  {rankingRefreshNote}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </Grid>
    </Grid>
  )
}
